/**
 * ConfirmDialog — FROZEN PUBLIC CONTRACT
 *
 * @public API (do not change without coordinating agent sign-off):
 *   @Input() title: string         (dialog heading, e.g. "Delete Charge?")
 *   @Input() description: string   (body text explaining the action)
 *   @Input() confirmLabel: string  (confirm button label, default "Confirm")
 *   @Input() cancelLabel: string   (cancel button label, default "Cancel")
 *   @Input() destructive: boolean  (true → confirm button uses destructive variant)
 *   @Output() confirmed: EventEmitter<void>
 *   @Output() cancelled: EventEmitter<void>
 *
 * IMPORTANT: This component MUST be used inside <ng-template brnDialogContent>
 * (the portal injection pattern). Using it as a direct child of <brn-dialog>
 * without the ng-template wrapper will cause BrnDialogRef injection failure.
 *
 * Usage in parent component template:
 *   <hlm-dialog>
 *     <button brnDialogTrigger hlmBtn variant="destructive" size="sm">Delete</button>
 *     <ng-template brnDialogContent>
 *       <hlm-dialog-content>
 *         <app-confirm-dialog
 *           title="Delete this charge?"
 *           description="This action cannot be undone."
 *           [destructive]="true"
 *           (confirmed)="onConfirm()"
 *           (cancelled)="dialogRef.close()"
 *         />
 *       </hlm-dialog-content>
 *     </ng-template>
 *   </hlm-dialog>
 */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  inject,
  input,
} from '@angular/core';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, ...HlmDialogImports],
  template: `
    <hlm-dialog-header>
      <h3 hlmDialogTitle>{{ title() }}</h3>
      @if (description()) {
        <p hlmDialogDescription>{{ description() }}</p>
      }
    </hlm-dialog-header>

    <hlm-dialog-footer>
      <button hlmBtn variant="outline" (click)="onCancel()">
        {{ cancelLabel() }}
      </button>
      <button
        hlmBtn
        [variant]="destructive() ? 'destructive' : 'default'"
        (click)="onConfirm()"
      >
        {{ confirmLabel() }}
      </button>
    </hlm-dialog-footer>
  `,
})
export class ConfirmDialogComponent {
  private readonly _dialogRef = inject(BrnDialogRef, { optional: true });

  readonly title = input.required<string>();
  readonly description = input<string>('');
  readonly confirmLabel = input<string>('Confirm');
  readonly cancelLabel = input<string>('Cancel');
  readonly destructive = input<boolean>(false);

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  onConfirm(): void {
    this.confirmed.emit();
    this._dialogRef?.close();
  }

  onCancel(): void {
    this.cancelled.emit();
    this._dialogRef?.close();
  }
}
